import React, { createContext, useState, useContext, useEffect } from 'react';
import { authApi } from '../api/authApi';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser.isAuthenticated) {
          setUser(parsedUser);
        }
      } catch (error) {
        console.error('Failed to parse user data:', error);
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = (userData, token = null) => {
    const userWithAuth = { ...userData, isAuthenticated: true };
    setUser(userWithAuth);
    localStorage.setItem('user', JSON.stringify(userWithAuth));

    // Store token if provided
    if (token) {
      localStorage.setItem('token', token);
    }
  };

  const logout = async () => {
    try {
      // Call backend to invalidate token
      const token = localStorage.getItem('token');
      if (token) {
        await authApi.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local cleanup even if API call fails
    } finally {
      // Clear local state and storage
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      // Navigation will be handled by the component that calls logout
    }
  };

  const hasMenuAccess = (menuKey) => Boolean(user?.menuAccess?.[menuKey]);

  const value = {
    user,
    loading,
    isAuthenticated: !!user?.isAuthenticated,
    login,
    logout,
    hasMenuAccess
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

